import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOrder } from '@/common/booking';
import {
    BannerRefund,
    BookingStep,
    ErrorBoundary,
    Footer,
    JourneysSection,
    StickyBar,
    TopBar,
    WidgetBar,
} from '@/sleeper/components';
import {
    useAddTrip,
    useCustomerFlexipassUnused,
    useForceSession,
    usePreservedSearchQuery,
    useSearchQuery,
    useSelectedFares,
    useSelectedFaresTotalPrice,
} from '@/sleeper/hooks';

export function FaresPage() {
  useForceSession();
  const { push } = useRouter();
  const { isLoading: isLoadingOrder } = useOrder();
  const { flexipassUnusedData } = useCustomerFlexipassUnused();
  const addTrip = useAddTrip();
  const [addTripError, setAddTripError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    outwardSelection,
    inwardSelection,
    isFlexipassSelected,
    clearSelection,
    setInwardSelection,
    setIsFlexipassSelected,
  } = useSelectedFares();
  const { searchQuery, queryString } = useSearchQuery();
  const { saveQuery } = usePreservedSearchQuery();
  const supplement = searchQuery.roomSupplement ? '?roomSupplement' : '';
  const [isEmptySelection, setIsEmptySelection] = useState(false);
  const setIsFlexipassSelectedAndClearSelection = useCallback(
    (isSelected: boolean) => {
      setIsFlexipassSelected(isSelected);
      clearSelection();
    },
    [clearSelection, setIsFlexipassSelected],
  );
  const {
    price,
    flexipassCost,
    isLoading: isLoadingPrice,
  } = useSelectedFaresTotalPrice();
  const isLoading = isLoadingOrder || isLoadingPrice;

  const numberOfChildren = useMemo(() => {
    const children5To11 = searchQuery.children5to11 || null;
    const children12To15 = searchQuery.children12to15 || null;

    return {
      children5To11: children5To11,
      children12To15: children12To15,
    };
  }, [searchQuery]);

  const onAddTrip = useCallback(async () => {
    if (
      !outwardSelection ||
      (!inwardSelection && searchQuery.inwardDate) ||
      (!outwardSelection && !searchQuery.inwardDate)
    ) {
      setIsEmptySelection(true);
    } else {
      if (!outwardSelection) {
        return;
      }

      setAddTripError(null);
      setIsSubmitting(true);
      saveQuery(queryString);

      const error = await addTrip(
        outwardSelection,
        inwardSelection || undefined,
        numberOfChildren,
        searchQuery.originalTickets,
      );

      setIsSubmitting(false);

      if (error) {
        console.error('Error adding trip:', error);
        setAddTripError('An error occurred while adding the trip.');
        return;
      }

      clearSelection();
      await push(`/guests${supplement}`);
    }
  }, [
    addTrip,
    clearSelection,
    inwardSelection,
    numberOfChildren,
    outwardSelection,
    push,
    queryString,
    saveQuery,
    searchQuery.inwardDate,
    searchQuery.originalTickets,
    supplement,
  ]);

  useEffect(() => {
    if (inwardSelection && !searchQuery.inwardDate) {
      setInwardSelection(null);
    }
  }, [inwardSelection, searchQuery.inwardDate, setInwardSelection]);

  return (
    <>
      <header className="header">
        <TopBar activeStep={BookingStep.JOURNEY} />
        <WidgetBar
          isFlexipassChecked={isFlexipassSelected}
          onFlexipassSwitchChange={setIsFlexipassSelectedAndClearSelection}
        />
      </header>

      <main className="content">
        <ErrorBoundary>
          {addTripError && (
            <section className="container">
              <div className="row">
                <div className="col-12 centered">
                  <div className="msg msg--error msg--md msg--ico mt-2">
                    {addTripError}{' '}
                    <span>
                      Please try again later or contact our customer service.
                    </span>
                  </div>
                </div>
              </div>
            </section>
          )}
          <JourneysSection
            isFlexipassSelected={isFlexipassSelected}
            isEmptySelection={isEmptySelection}
            flexipassUnusedData={flexipassUnusedData}
          />
        </ErrorBoundary>
      </main>

      {/* TODO: DRY it with calendar.tsx */}
      <StickyBar
        nextStepName="Guest Details"
        onCtaClick={onAddTrip}
        isCtaDisabled={isSubmitting}
        isLoading={isLoading}
        totalPrice={price}
        totalFlexipassCost={flexipassCost}
        isUsingFlexipass={isFlexipassSelected}
      />

      <BannerRefund />
      <Footer />
    </>
  );
}

export default FaresPage;
